export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/phsZrvT7LLKVY6Ft32Mwvu/ChatGPT_Image_Jul_10,_2026,_05_46_56_PM.png'

export const BONUS_PREVIEWS = [
  'https://cdn.magicpatterns.com/patterns/generated-images/4edec617-284e-4c68-a43e-3a399dcebec0.jpg',
  'https://cdn.magicpatterns.com/patterns/generated-images/d5645c6a-ae20-4e65-b767-9268758e6a36.jpg',
  'https://cdn.magicpatterns.com/patterns/generated-images/b1de6f8b-1cd6-448f-88c1-fb657e2afb3e.jpg',
  'https://cdn.magicpatterns.com/patterns/generated-images/84ccc5b1-e0ea-48f4-9c31-89eb65ab1d42.jpg',
  'https://cdn.magicpatterns.com/patterns/generated-images/a622576e-e720-40e9-bbf2-cc3e5cc19210.jpg',
]

export const PROBLEMS: string[] = [
  'You spend hours planning practices, yet the kids still get bored and lose focus.',
  'You are not sure what to teach first: technique, tactics, or the game itself?',
  'Every practice feels improvised, without a clear development plan.',
  'Parents expect results, but you do not have a clear way to show progress.',
  'You coach the way you played, without a proven system to back you up.',
]

export interface ModuleItem {
  emoji: string
  title: string
  body: string[]
}

export const MODULES: ModuleItem[] = [
  {
    emoji: '🎯',
    title:
      'Volume 1 — Technical Fundamentals: Shooting, Ball Handling, and Defense (55 pages)',
    body: [
      'Why technique is the first line of prevention, not just performance.',
      'Shooting mechanics: base, elbow alignment, and follow-through — plus the errors that create shoulder overload.',
      'Ball handling: hip and knee posture during explosive direction changes.',
      'Defensive stance: why poor base positioning is a hidden cause of ankle sprains.',
      'Jump and landing mechanics: the most critical checkpoint in the entire sport.',
      'Rebounding: aerial contact technique and safe landing in traffic.',
      'Cutting and stopping: biomechanics of direction changes and their link to knee injury.',
      'Video self-assessment: 6 technical checkpoints any player can do alone.',
      'Most common technical errors by position: guard, forward, and center.',
    ],
  },
  {
    emoji: '🛡️',
    title: 'Volume 2 — Injury Prevention by Specific Movement (55 pages)',
    body: [
      'Why basketball concentrates so many ankle and knee injuries.',
      'Ankle sprains: injury mechanics and why they are the sport’s most common injury.',
      'Ankle prevention protocol: proprioception and stabilizer strength.',
      'Knee: patellar tendinitis (jumper’s knee) from repeated jump overuse.',
      'Knee prevention protocol: neuromuscular control on landing.',
      'ACL injuries: why basketball has one of the highest incidence rates and how non-contact injuries happen.',
      'ACL risk-reduction protocol: landing, deceleration, and hamstring strength.',
      'Fingers and wrist: jamming from ball contact and a management protocol.',
      'Shoulder: overuse from repetitive shooting during an intense preseason.',
      'Quick-reference table: pain area → likely cause → matching protocol.',
      'Warning signs: when pain is normal training soreness and when to stop.',
    ],
  },
  {
    emoji: '💪',
    title: 'Volume 3 — Strength, Jump, and Conditioning Program (55 pages)',
    body: [
      'Periodization for amateur and school players: preseason, in-season, and postseason.',
      'Phase 1 (Weeks 1–4): strength base and ankle/knee stability.',
      'Phase 2 (Weeks 5–8): vertical-jump power built on safe landing mechanics.',
      'Phase 3 (Weeks 9–12): sport-specific conditioning, repeated-sprint ability, and transitions.',
      'Week-by-week routines with sets, reps, and progression.',
      'Dynamic pre-practice and pregame warm-up (12–15 minutes).',
      'Core and hip work: the hidden base of jump power.',
      'Postgame cooldown and recovery.',
      'How to measure real progress: jump height and change-of-direction speed, not just how it feels.',
    ],
  },
  {
    emoji: '🧠',
    title:
      'Volume 4 — Psychology of the Player: The Mind Under Pressure (45 pages)',
    body: [
      'Why basketball is a game of split-second decisions made under constant pressure.',
      'Managing pressure on free throws and decisive moments: final seconds and playoffs.',
      'Free-throw routine: build a ritual that cuts anxiety.',
      'Focus and mental reset after a mistake: turnover, missed shot, or foul.',
      'Technical confidence vs. in-game confidence: why shooting well in practice guarantees nothing in a game.',
      'Managing frustration with referees, opponents, and teammates without losing focus.',
      'Performance anxiety in young players: recognizing it and working through it.',
      'The mental return after an injury: fear of re-injury on the first contact back.',
      'Applied visualization: a simple pregame and pre-shot protocol.',
      'Communicating with a coach or parents: asking for help without it feeling like weakness.',
    ],
  },
  {
    emoji: '🌱',
    title:
      'Volume 5 — Basketball in Young Athletes: A Guide for Developing Players and Their Families (45 pages)',
    body: [
      'Why a growing body responds differently to the same training.',
      'Growth plates: what they are and why overuse is riskier at this stage.',
      'Osgood-Schlatter disease: the most common injury in teenage basketball players and how to recognize it.',
      'Early specialization: the risks of playing the same sport year-round without rest.',
      'Safe training load by age: a practical reference guide.',
      'Signs of overtraining in young athletes that they may not report themselves.',
      'A guide for parents and coaches: supporting without overprotecting or over-pressuring.',
      'Basic nutrition and hydration for the growing young player: a general framework, not diet plans.',
      'When to consult a pediatric sports physician.',
      'The difficult conversation: what to say to your child after a bad performance.',
    ],
  },
  {
    emoji: '🔄',
    title: 'Volume 6 — Returning to the Court After an Injury (40 pages)',
    body: [
      'Why returning too early is the most common mistake in amateur basketball.',
      'General principles of a progressive return to sport.',
      'Return protocol: ankle sprain.',
      'Return protocol: knee tendinitis or post-ACL, with medical clearance.',
      'Return protocol: shoulder.',
      'Return protocol: fingers and wrist.',
      'Green/yellow/red progression checklist to safely advance through phases.',
      'When return to play requires medical clearance.',
      'Relapse prevention: permanent technique and training-load adjustments.',
    ],
  },
]

export const MODULE_GROUPS = [
  { label: 'PLAYER HEALTH & PERFORMANCE', range: [0, 6] as const },
]

export interface BonusItem {
  emoji: string
  title: string
  body: string
  preview: string
}

export const BONUSES: BonusItem[] = [
  {
    emoji: '🎯',
    title: 'Bonus 1 — Shooting Checklist',
    body: 'A simple personal checklist for balance, elbow alignment, follow-through, consistency, and foot position.',
    preview: BONUS_PREVIEWS[0],
  },
  {
    emoji: '🛡️',
    title: 'Bonus 2 — Injury Risk Guide',
    body: 'Spot common ankle, knee, shoulder, and wrist risk factors before they turn into time away from the court.',
    preview: BONUS_PREVIEWS[1],
  },
  {
    emoji: '🧠',
    title: 'Bonus 3 — Pressure Routine for Free Throws',
    body: 'A repeatable breathe, visualize, ritual, and reset routine for more confidence at the line.',
    preview: BONUS_PREVIEWS[2],
  },
  {
    emoji: '🌱',
    title: 'Bonus 4 — Young Athlete Guide',
    body: 'A practical guide for safe training volume, growth, recovery, nutrition basics, and warning signs.',
    preview: BONUS_PREVIEWS[3],
  },
  {
    emoji: '📈',
    title: 'Bonus 5 — 12-Week Progression Tracker',
    body: 'Track strength, vertical jump, agility, training load, and ankle or knee discomfort week by week.',
    preview: BONUS_PREVIEWS[4],
  },
]

export interface Review {
  name: string
  location: string
  text: string
}

export const REVIEWS: Review[] = [
  {
    name: 'Marcus Thompson',
    location: 'Indianapolis, IN',
    text: 'This completely changed how I run my youth practices. My kids are finally having fun AND improving. Worth every penny.',
  },
  {
    name: 'Jason Miller',
    location: 'Columbus, OH',
    text: 'The 12-week ready-to-use plan alone saved me hours every week. My assistant coaches love the structure.',
  },
  {
    name: 'David Reynolds',
    location: 'Sacramento, CA',
    text: 'I coached the way I played for years. This gave me an actual method. Parents are noticing the difference.',
  },
  {
    name: 'Anthony Carter',
    location: 'Charlotte, NC',
    text: 'The scouting and stats sheets made me look like a pro. Best $25 I ever spent on coaching.',
  },
]

export interface Testimonial {
  name: string
  role: string
  location: string
  text: string
}

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Coach Ryan Walsh',
    role: 'Middle School Coach, 34',
    location: 'Austin, TX',
    text: 'Finally a system that tells me WHAT to teach and WHEN. My practices have never been this organized.',
  },
  {
    name: 'Coach Kevin Brooks',
    role: 'AAU Program Director, 41',
    location: 'Atlanta, GA',
    text: 'The injury-prevention protocols in Volume 2 changed how I prepare my players. Incredible value.',
  },
  {
    name: 'Coach Michael Dunn',
    role: 'High School JV Coach, 29',
    location: 'Denver, CO',
    text: 'Downloaded it, printed it, used it the same night. Immediate delivery is no joke.',
  },
  {
    name: 'Coach Steven Ortiz',
    role: 'Youth League Coach, 38',
    location: 'Phoenix, AZ',
    text: 'The parent communication templates alone ended half of my headaches this season.',
  },
  {
    name: 'Coach Brandon Hayes',
    role: 'Club Coach, 45',
    location: 'Portland, OR',
    text: 'I went from improvising every session to having a real plan. My players respect the structure.',
  },
]

export interface Objection {
  q: string
  a: string
}

export const OBJECTIONS: Objection[] = [
  {
    q: 'What if I do not have coaching experience?',
    a: 'That is exactly what this system is designed for. It starts with step-by-step youth progressions and never assumes you already know the answer.',
  },
  {
    q: 'How do I receive the material?',
    a: 'Everything is 100% digital in PDF format. Once your purchase is complete, you will get instant email access to download it all.',
  },
  {
    q: 'Will it work for youth and adult teams?',
    a: 'Yes. The 6 volumes cover technical fundamentals, injury prevention, performance, psychology, youth development, and return to play for basketball athletes.',
  },
  {
    q: 'Can I print the templates?',
    a: 'Absolutely. Every template and bonus is designed to print and use on the court from day one.',
  },
  {
    q: 'What if it is not right for me?',
    a: 'Today’s price can pay for itself with just one better-planned practice. The risk is small compared with everything included.',
  },
]

/* For the sold-notification popup */
export const RANDOM_BUYERS: { name: string; city: string }[] = [
  { name: 'James W.', city: 'Chicago, IL' },
  { name: 'Robert M.', city: 'Houston, TX' },
  { name: 'Michael B.', city: 'Miami, FL' },
  { name: 'William K.', city: 'Seattle, WA' },
  { name: 'Daniel R.', city: 'Boston, MA' },
  { name: 'Christopher L.', city: 'Las Vegas, NV' },
  { name: 'Matthew S.', city: 'Nashville, TN' },
  { name: 'Andrew P.', city: 'San Diego, CA' },
  { name: 'Joshua T.', city: 'Detroit, MI' },
  { name: 'Ryan H.', city: 'Kansas City, MO' },
]
